import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { ConfirmDeleteModal, LoadState, PlainText } from "./AdminShared";

const emptyBlog = { title: "", date: "", text: "", album: "" };

const BlogAdmin = () => {
    const [blogs, setBlogs] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [notice, setNotice] = useState("");
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [blogData, setBlogData] = useState(emptyBlog);

    const token = localStorage.getItem("token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setLoadError("");
            try {
                const [blogResponse, albumResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/blog/`, auth),
                    axios.get(`${process.env.REACT_APP_API_URL}/albums/`, auth),
                ]);
                setBlogs(blogResponse.data.results || blogResponse.data || []);
                setAlbums(albumResponse.data.results || albumResponse.data || []);
            } catch {
                setLoadError("Blogs konnten nicht geladen werden. Bitte versuche es erneut.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const filteredBlogs = useMemo(() => {
        const query = search.trim().toLocaleLowerCase("de");
        return [...blogs]
            .filter((blog) => !query
                || blog.title?.toLocaleLowerCase("de").includes(query)
                || blog.text?.toLocaleLowerCase("de").includes(query))
            .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    }, [blogs, search]);

    const openModal = (blog = null) => {
        setSelectedBlog(blog);
        setBlogData(blog ? {
            title: blog.title || "",
            date: blog.date || "",
            text: blog.text || "",
            album: blog.album || "",
        } : emptyBlog);
        setSaveError("");
        setShowModal(true);
    };

    const closeModal = () => {
        if (!saving) setShowModal(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setSaveError("");
        const payload = {
            title: blogData.title,
            date: blogData.date,
            text: blogData.text,
            album: blogData.album || null,
        };
        try {
            const response = selectedBlog
                ? await axios.put(`${process.env.REACT_APP_API_URL}/blog/${selectedBlog.id}/`, payload, auth)
                : await axios.post(`${process.env.REACT_APP_API_URL}/blog/`, payload, auth);
            const saved = response.data;
            setBlogs((current) => selectedBlog
                ? current.map((blog) => blog.id === selectedBlog.id ? saved : blog)
                : [saved, ...current]);
            setShowModal(false);
            setNotice(selectedBlog ? "Blogbeitrag wurde aktualisiert." : "Blogbeitrag wurde erstellt.");
        } catch {
            setSaveError("Der Blogbeitrag konnte nicht gespeichert werden. Bitte prüfe die Eingaben.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/blog/${deleteTarget.id}/`, auth);
            setBlogs((current) => current.filter((blog) => blog.id !== deleteTarget.id));
            setDeleteTarget(null);
            setNotice("Blogbeitrag wurde gelöscht.");
        } catch {
            setNotice("");
            setLoadError("Der Blogbeitrag konnte nicht gelöscht werden.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section aria-labelledby="blog-admin-title">
            <div className="admin-page-heading">
                <div>
                    <p className="admin-eyebrow">Inhalte</p>
                    <h1 id="blog-admin-title">Blogs</h1>
                    <p>Beiträge erstellen, bearbeiten und mit einem Album verknüpfen.</p>
                </div>
                <Button onClick={() => openModal()}>Neuer Blogbeitrag</Button>
            </div>

            {notice && <Alert variant="success" dismissible onClose={() => setNotice("")}>{notice}</Alert>}
            <div className="admin-toolbar">
                <Form.Control
                    type="search"
                    aria-label="Blogs durchsuchen"
                    placeholder="Titel und Text durchsuchen …"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            <LoadState
                loading={loading}
                error={loadError}
                empty={filteredBlogs.length === 0}
                emptyText={search ? "Keine Blogbeiträge passen zu deiner Suche." : "Noch keine Blogbeiträge vorhanden."}
            >
                <div className="admin-list">
                    {filteredBlogs.map((blog) => {
                        const albumTitle = albums.find((album) => String(album.id) === String(blog.album))?.title;
                        return (
                            <article className="admin-item" key={blog.id}>
                                <div className="admin-item__header">
                                    <div>
                                        <h2 className="admin-item__title">{blog.title}</h2>
                                        <div className="admin-item__meta">
                                            <span>{blog.date ? new Date(`${blog.date}T00:00:00`).toLocaleDateString("de-DE") : "Ohne Datum"}</span>
                                            {albumTitle && <span>Album: {albumTitle}</span>}
                                        </div>
                                    </div>
                                </div>
                                <PlainText className="admin-item__description">{blog.text}</PlainText>
                                <div className="admin-item__footer">
                                    <span className="admin-status">{albumTitle ? "Mit Album" : "Ohne Album"}</span>
                                    <div className="admin-actions">
                                        <Button variant="outline-secondary" onClick={() => openModal(blog)}>Bearbeiten</Button>
                                        <Button variant="outline-danger" onClick={() => setDeleteTarget(blog)}>Löschen</Button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </LoadState>

            <Modal
                show={showModal}
                onHide={closeModal}
                centered size="lg"
                className="admin-editor-modal"
                backdropClassName="admin-modal-backdrop"
            >
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>{selectedBlog ? "Blogbeitrag bearbeiten" : "Blogbeitrag erstellen"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {saveError && <Alert variant="danger">{saveError}</Alert>}
                    <Form id="blog-editor-form" onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="blog-title">
                            <Form.Label>Titel</Form.Label>
                            <Form.Control
                                value={blogData.title}
                                onChange={(event) => setBlogData({ ...blogData, title: event.target.value })}
                                required
                                autoFocus
                                disabled={saving}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="blog-date">
                            <Form.Label>Datum</Form.Label>
                            <Form.Control
                                type="date"
                                value={blogData.date}
                                onChange={(event) => setBlogData({ ...blogData, date: event.target.value })}
                                required
                                disabled={saving}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="blog-text">
                            <Form.Label>Text</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={8}
                                value={blogData.text}
                                onChange={(event) => setBlogData({ ...blogData, text: event.target.value })}
                                required
                                disabled={saving}
                            />
                            <Form.Text>Zeilenumbrüche und leere Absätze werden in der Veröffentlichung beibehalten.</Form.Text>
                        </Form.Group>
                        <Form.Group controlId="blog-album">
                            <Form.Label>Album <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Select
                                value={blogData.album}
                                onChange={(event) => setBlogData({ ...blogData, album: event.target.value })}
                                disabled={saving}
                            >
                                <option value="">Kein Album</option>
                                {albums.map((album) => <option key={album.id} value={album.id}>{album.title}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={closeModal} disabled={saving}>Abbrechen</Button>
                    <Button type="submit" form="blog-editor-form" disabled={saving}>
                        {saving && <Spinner animation="border" size="sm" className="me-2" />}
                        Speichern
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmDeleteModal
                show={Boolean(deleteTarget)}
                title={deleteTarget?.title || ""}
                busy={deleting}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            />
        </section>
    );
};

export default BlogAdmin;
